import { paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import {
  BulkAssignVendor_Create,
  DriverAssignment_Create,
  putV1CompaniesCompanyIdJobsBulkAssignDrivers,
  PutV1CompaniesCompanyIdJobsBulkAssignDriversData,
  putV1CompaniesCompanyIdJobsBulkAssignVendors,
  PutV1CompaniesCompanyIdJobsBulkAssignVendorsData,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import { ComplianceWarningDialog } from '~components/CompliancePanel/ComplianceWarningDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogV2Content from '~components/Dialog/DialogContent';
import useAssignDriverMenu from '~components/Job/AssignJobToView/hooks/useAssignDriverMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { JobAssignmentType } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import useComplianceCheck, {
  ComplianceCheckResult,
} from '~hooks/useComplianceCheck/useComplianceCheck';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { useStores } from '~store';
import { SelectedAssignee } from '~store/BulkAssignMultipleStore';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { isJobAssignable } from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AssigneesList from './bulk-assign-multiple-jobs-dialog/AssigneesList';
import AssigneeTabs from './bulk-assign-multiple-jobs-dialog/AssigneeTabs';
import DialogHeader from './bulk-assign-multiple-jobs-dialog/DialogHeader';
import Search from './bulk-assign-multiple-jobs-dialog/Search';
import SelectionCounters from './bulk-assign-multiple-jobs-dialog/SelectionCounters';

const DIALOG_CONTENT_INNER_GUTTER_SIZE = 1.5;
const DIALOG_CONTENT_FLEX_GAP_SIZE = 1.5;

const distributeJobs = (assignees: SelectedAssignee[], jobs: Job[]) => {
  let driversAssignments: DriverAssignment_Create[] = [];
  const vendorsAssignments: BulkAssignVendor_Create[] = [];
  let currentJobIndex = 0;
  const sortedJobs = [...jobs].sort((a, b) => a.jobStartAt.unix() - b.jobStartAt.unix());

  assignees.forEach((assignee) => {
    const jobsAssignedCount = assignee.count;
    const hasJobsAssigned = jobsAssignedCount > 0;
    const isJobForVendor = assignee.type === JobAssignmentType.VENDOR;

    if (hasJobsAssigned) {
      if (isJobForVendor) {
        const assignment: BulkAssignVendor_Create = {
          vendor_account_id: assignee.id,
          job_ids: Array.from(
            { length: jobsAssignedCount },
            (_, i) => sortedJobs[currentJobIndex + i].id,
          ),
        };

        vendorsAssignments.push(assignment);
      } else {
        const assignments = Array.from(
          { length: jobsAssignedCount },
          (_, i): DriverAssignment_Create => ({
            driver_id: assignee.id,
            job_id: sortedJobs[currentJobIndex + i].id,
          }),
        );

        driversAssignments = driversAssignments.concat(assignments);
      }

      currentJobIndex = currentJobIndex + jobsAssignedCount;
    }
  });

  return { driversAssignments, vendorsAssignments };
};

interface BulkAssignMultipleJobsDialogProps {
  filterDate?: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  order?: Order;
}

const BulkAssignMultipleJobsDialog = observer(
  ({
    filterDate,
    isOpen,
    onClose,
    onConfirm,
    order,
  }: BulkAssignMultipleJobsDialogProps) => {
    const { userStore, bulkAssignMultipleStore, ordersDispatchStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const {
      isLoading: isCheckingCompliance,
      check: checkCompliance,
      reset: resetCompliance,
    } = useComplianceCheck();
    const [showComplianceWarning, setShowComplianceWarning] = useState(false);
    const [combinedComplianceResult, setCombinedComplianceResult] =
      useState<ComplianceCheckResult | null>(null);

    const orderJobs = order?.id
      ? (ordersDispatchStore.orderJobs.get(order.id) ?? [])
      : [];
    const orderAssignableJobs = orderJobs.filter((job) => isJobAssignable(job));

    const noAssigneesSelected = bulkAssignMultipleStore.selectedAssigneesCount === 0;
    const selectedAssigneesCountExceedsMaxSelectionsCount =
      bulkAssignMultipleStore.selectedAssigneesCount > orderAssignableJobs.length;
    const shouldDisableConfirmButton = order
      ? noAssigneesSelected || selectedAssigneesCountExceedsMaxSelectionsCount
      : false;

    const handleClearAllClick = useCallback(() => {
      bulkAssignMultipleStore.clearSelectedAssignees();
    }, []);

    const executeAssignments = useCallback(
      async (complianceOverride?: boolean) => {
        const { driversAssignments, vendorsAssignments } = distributeJobs(
          bulkAssignMultipleStore.selectedAssignees,
          orderAssignableJobs,
        );

        const promises: Promise<unknown>[] = [];

        if (driversAssignments.length) {
          const driversBody = {
            driver_assignments: driversAssignments,
            ...(complianceOverride ? { compliance_override: true } : {}),
          };
          promises.push(
            putV1CompaniesCompanyIdJobsBulkAssignDrivers({
              body: driversBody as NonNullable<
                PutV1CompaniesCompanyIdJobsBulkAssignDriversData['body']
              >,
              path: { 'company-id': companyId },
            }),
          );
        }

        if (vendorsAssignments.length) {
          const vendorsBody = {
            vendor_assignments: vendorsAssignments,
            ...(complianceOverride ? { compliance_override: true } : {}),
          };
          promises.push(
            putV1CompaniesCompanyIdJobsBulkAssignVendors({
              body: vendorsBody as NonNullable<
                PutV1CompaniesCompanyIdJobsBulkAssignVendorsData['body']
              >,
              path: { 'company-id': companyId },
            }),
          );
        }

        await Promise.all(promises);
        // Driver assignments are one record per job, while each vendor assignment
        // carries a job_ids array, so vendor jobs must be summed to count jobs
        // actually submitted (not every assignable job on the order).
        const driverJobCount = driversAssignments.length;
        const vendorJobCount = vendorsAssignments.reduce(
          (total, assignment) => total + assignment.job_ids.length,
          0,
        );
        AnalyticsController.track('job_assignment_submitted', {
          method: 'bulk_dialog',
          assignee_type:
            driverJobCount && vendorJobCount
              ? 'mixed'
              : driverJobCount
                ? 'driver'
                : 'vendor',
          job_count: driverJobCount + vendorJobCount,
          driver_assignment_count: driverJobCount,
          vendor_assignment_count: vendorJobCount,
        });
        onConfirm();
      },
      [
        JSON.stringify(bulkAssignMultipleStore.selectedAssignees),
        companyId,
        orderAssignableJobs,
        onConfirm,
      ],
    );

    const handleConfirm = useCallback(async () => {
      const vendorAssignees = bulkAssignMultipleStore.selectedAssignees.filter(
        (a) => a.type === JobAssignmentType.VENDOR && a.count > 0,
      );
      const driverAssignees = bulkAssignMultipleStore.selectedAssignees.filter(
        (a) => a.type === JobAssignmentType.DRIVER && a.count > 0,
      );

      const [vendorResults, driverResults] = await Promise.all([
        Promise.all(
          vendorAssignees.map((a) => checkCompliance(companyId, a.id, undefined)),
        ),
        Promise.all(
          driverAssignees.map((a) => checkCompliance(companyId, undefined, a.id)),
        ),
      ]);
      const allResults = [...vendorResults, ...driverResults];

      const hasAnyIssues = allResults.some((r) => r.hasIssues);

      if (hasAnyIssues) {
        const hasError = allResults.some((r) => r.hasIssues);
        setCombinedComplianceResult({
          hasIssues: true,
          warnings: allResults.flatMap((r) => r.warnings),
          blocking: allResults.flatMap((r) => r.blocking),
          ...(hasError ? { error: true } : {}),
        });
        resetCompliance();
        setShowComplianceWarning(true);
        return;
      }

      await executeAssignments();
    }, [
      JSON.stringify(bulkAssignMultipleStore.selectedAssignees),
      checkCompliance,
      companyId,
      executeAssignments,
      resetCompliance,
    ]);

    const [isOverrideSubmitting, setIsOverrideSubmitting] = useState(false);

    const handleComplianceOverride = useCallback(async () => {
      setIsOverrideSubmitting(true);
      try {
        await executeAssignments(true);
      } finally {
        setIsOverrideSubmitting(false);
        setShowComplianceWarning(false);
        setCombinedComplianceResult(null);
        resetCompliance();
      }
    }, [executeAssignments, resetCompliance]);

    const handleComplianceCancel = useCallback(() => {
      setShowComplianceWarning(false);
      setCombinedComplianceResult(null);
      resetCompliance();
    }, [resetCompliance]);

    const handleClose = useCallback(() => {
      setShowComplianceWarning(false);
      setCombinedComplianceResult(null);
      resetCompliance();
      onClose();
    }, [onClose, resetCompliance]);

    return (
      <>
        <Dialog
          isOpen={isOpen}
          onClose={handleClose}
          preventCloseOnBackdropClick
          sx={{ [`.${paperClasses.root}`]: { height: '720px', maxWidth: '649px' } }}
        >
          <DialogV2Content
            sx={{
              '& > div': {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                pb: 0,
                pt: 1.5,
                px: 0,
              },
            }}
          >
            {order && (
              <DialogContent
                assignableJobs={orderAssignableJobs}
                date={filterDate}
                order={order}
              />
            )}
          </DialogV2Content>

          <DialogActions>
            <SmallButton
              color="brandV2OrangeDark"
              onClick={handleClearAllClick}
              variant="text"
              sx={{
                mr: 'auto',
                visibility: bulkAssignMultipleStore.selectedAssigneesCount
                  ? 'visible'
                  : 'hidden',
              }}
            >
              {t('common.clear_all')}
            </SmallButton>

            <SmallButton
              color="secondary"
              disabled={isCheckingCompliance}
              onClick={handleClose}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton
              color="brandV2Yellow"
              disabled={shouldDisableConfirmButton}
              loading={isCheckingCompliance}
              onClick={handleConfirm}
            >
              {t('dispatch.order.assign_all')}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>

        {combinedComplianceResult && (
          <ComplianceWarningDialog
            isOpen={showComplianceWarning}
            result={combinedComplianceResult}
            isSubmitting={isOverrideSubmitting}
            onCancel={handleComplianceCancel}
            onOverride={handleComplianceOverride}
          />
        )}
      </>
    );
  },
);

interface DialogContentProps {
  assignableJobs: Job[];
  date?: string;
  order: Order;
}

const DialogContent = observer(({ assignableJobs, date, order }: DialogContentProps) => {
  const { userStore, bulkAssignMultipleStore } = useStores();

  const companyId = getEffectiveUserCompanyId(userStore);
  const countsByType = bulkAssignMultipleStore.selectedAssigneesCountByAssigneeType;

  const switchAssigneeTabsOrderFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.switchAssigneeTabsOrder,
  });

  const {
    closeMenu,
    data,
    fetchMore,
    hasMore,
    loadingReason,
    openMenu,
    searchBarRef,
    searchFieldValue,
    selectedTab,
    setSearchFieldValue,
    setSelectedTab,
  } = useAssignDriverMenu(
    companyId,
    switchAssigneeTabsOrderFeatureFlagEnabled ? JobAssignmentType.VENDOR : undefined,
    date,
  );

  const hasEmptyResults = searchFieldValue.length > 0 && !loadingReason && !data?.length;

  useEffect(() => {
    openMenu();
    bulkAssignMultipleStore.clearSelectedAssignees();

    return () => {
      closeMenu();
    };
  }, [openMenu, closeMenu]);

  return (
    <>
      <Box sx={{ flex: 0 }}>
        <DialogHeader
          maxSelectionsCount={assignableJobs.length}
          order={order}
          sx={{ px: DIALOG_CONTENT_INNER_GUTTER_SIZE, mb: 1.5 }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: DIALOG_CONTENT_FLEX_GAP_SIZE,
          }}
        >
          <AssigneeTabs
            driversCount={countsByType[JobAssignmentType.DRIVER]}
            onChange={setSelectedTab}
            selectedTab={selectedTab}
            vendorsCount={countsByType[JobAssignmentType.VENDOR]}
            sx={{ px: DIALOG_CONTENT_INNER_GUTTER_SIZE, mt: 1.5 }}
          />

          <Search
            ref={searchBarRef}
            assigneeType={selectedTab}
            onChange={setSearchFieldValue}
            sx={{ px: DIALOG_CONTENT_INNER_GUTTER_SIZE }}
          />

          <SelectionCounters
            currentSelectionsCount={bulkAssignMultipleStore.selectedAssigneesCount}
            maxSelectionsCount={assignableJobs.length}
            sx={{ px: DIALOG_CONTENT_INNER_GUTTER_SIZE }}
          />

          <Divider />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: DIALOG_CONTENT_INNER_GUTTER_SIZE,
          py: DIALOG_CONTENT_FLEX_GAP_SIZE,
        }}
      >
        <AssigneesList
          key={selectedTab}
          assigneeType={selectedTab}
          assignees={data ?? []}
          fetchMore={fetchMore}
          hasMore={hasMore}
          loadingReason={loadingReason ?? undefined}
          showNoResultsMessage={hasEmptyResults}
        />
      </Box>
    </>
  );
});

export default BulkAssignMultipleJobsDialog;
